import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Drawer, Box, Button, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useConfirmAnalyses from 'hooks/useConfirmAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { confirmAnalysesSelectors } from 'features/confirmAnalyses';

const ConfirmAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { confirm_analysis_id } = useParams();
  const { getConfirmAnalysis, clearLocalActiveConfirmAnalysis } = useConfirmAnalyses();
  const { getConfirmAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const confirmAnalysisFetching = useCustomSelector(confirmAnalysesSelectors.confirmAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [confirmTimeEventsOpened, setConfirmTimeEventsOpened] = useState(true);

  const fetchConfirmAnalysis = useCallback((id) => {
    setOpen(true);
    getConfirmAnalysis({ id })
  }, [getConfirmAnalysis]);


  const handleExiting = () => {
    setExpanded(false);
    setConfirmTimeEventsOpened(true);
    navigate(`/flow/fl09c`);
    clearLocalActiveConfirmAnalysis();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchConfirmAnalysesEvents = useCallback((offset, confirm_analysis_id) => {
    getConfirmAnalysesEvents({ offset, confirm_analysis_id })
  }, [ getConfirmAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (confirmTimeEventsOpened) {
      fetchConfirmAnalysesEvents(0, confirm_analysis_id);
      setConfirmTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!confirm_analysis_id) fetchConfirmAnalysis(confirm_analysis_id);
  }, [confirm_analysis_id, fetchConfirmAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { confirmAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {confirm_analysis_id}</Typography>
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={confirm_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchConfirmAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ConfirmAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);